'use strict';

namespace Core {

    type HotelFromSessionService = Core.Hotels.HotelFromSessionService;
    type HotelWithRooms = Core.Hotels.HotelWithRooms;

    export interface CostBreakdownCodes {
        Hotel: number;
        CancellationProtection: number;
    }

    class BookingSummary {

        private readonly basketId :string;
        private readonly tripId :string;

        public atolAvailable: boolean;
        private atolCost: number;
        public cardFeeForDeposit: number;
        public cardFeeForTotal: number;
        public continueToNextStep: () => void;
        public priceDifference :number;
        public modifyEssentials :boolean;
        public readonly showContinueButton :boolean;
        private trip: any;
        public occupancy: Core.Trip.Occupancy;
        public attraction: any;
        public carhire: any;
        public flight: any;
        public hotel: HotelWithRooms;
        public insurance: any;
        public parking: any;
        public quoteRef: string;
        public quoteNo: string;
        public modifyEssentialsRoute: string;

        public transfer: any;
        private costBreakdown: any;
        private totalCost: any;
        private totalDiscount: any;
        private totalDeposit: any;
        private flightCost: number;
        private hotelCost: number;
        private cancellationProtection: number;
        private extrasCost: number;
        private hasDeposit: boolean;
        private hasPayDirect: boolean;
        private packagePrice: boolean;
        private changeFlightEnabled: string | boolean;
        private isKidsFree: boolean;
        private tax: any;
        private carHireExtras: any;
        private carHireExtrasTotal: number;
        private currencyCode: string;
        private extras: any;
        private flightCostBreakdown: any;
        private hotelCostBreakdown: any;
        private hotelExtrasCostBreakdown: any;
        private isMultiDestHoliday: boolean = false;
        private cityTaxTypeId: number = 22; // typeId of cityTax product in cost breakdown list
        private voucherDiscountMsg = { Message: '', Code: 0 };
        private voucherSuccessMsg = '';
        private isDiscountMsgPositive = false;
        private attractionsCost: number;
        private mandatoryTransferTypeId: number = 23;
        private hotelExtrasTypeIds: number[] = [1, 23, 24, 25, 26];
        private mandatoryTransfersCost: number = 0;

        constructor(
            private $q: ng.IQService,
            private $routeParams: any,
            private $scope: ng.IScope,
            private analytics: any,
            private basketStore: any,
            private broker: Broker,
            private hotelFromSession: HotelFromSessionService,
            private tripService: TripService,
            private taxationService: TaxationService,
            private costBreakdownCodes: CostBreakdownCodes,
            private occupancyService: any,
            private cookieService: any
        ) {
            this.voucherDiscountMsg = JSON.parse(cookieService.get('VoucherWarnMessage')
                || '{ "Message": "", "Code": 0 }') || { Message: '', Code: 0 };

            switch (this.voucherDiscountMsg!.Code!) {
                case 3:
                    this.voucherSuccessMsg = 'Promotional discount applied';
                    this.isDiscountMsgPositive = true;
                    break;
                case 5:
                    this.voucherSuccessMsg = 'Voucher code discount updated';
                    this.isDiscountMsgPositive = true;
                    break;
                case 6:
                    this.voucherSuccessMsg = 'Voucher code discount applied';
                    this.isDiscountMsgPositive = true;
                    break;
                case 7:
                    this.voucherSuccessMsg = 'Voucher code discount removed';
                    this.isDiscountMsgPositive = true;
                    break;
                default:
                    this.voucherSuccessMsg = '';
                    this.isDiscountMsgPositive = false;
                    break;
            }

            this.changeFlightEnabled = this.changeFlightEnabled === 'true';
            tripService.getTrip($routeParams.tripId).then(trip => {
                if (trip && trip.BasketId) {
                    this.trip = trip;
                    //we can have two ever so slightly different modes that this directive is used in
                    //1. as part of a holiday booking search
                    //2. as part of a quote which has no trip info just a basket
                    //we need to remove the reliance on the trip if possible

                    this.getProductInfo(trip.BasketId, $routeParams.tripId);
                    this.getCostBreakdown(trip.BasketId);

                    //checks we have a number of optional attributes, so here are some rules
                    //to keep us in check
                    if (_.isUndefined(this.cardFeeForDeposit) !== _.isUndefined(this.cardFeeForTotal)) {
                        throw Error('BookingSummary directive, both CardFee attributes should be supplied or neither.');
                    }

                    // Multi destination holidays, 'change flight' starts a new workflow which needs a loading screen
                    if (trip && trip.HotelsRequest && trip.HotelsRequest.Destinations) {
                        this.isMultiDestHoliday = true;
                    }
                }
            });
            this.showContinueButton = !!this.continueToNextStep;
            const init = (basketId :string, tripId :string) => {
                this.getProductInfo(basketId, tripId);
                this.getCostBreakdown(basketId);

                broker.subscribe('BookingSummary', 'basketUpdated', () => {
                    this.getCostBreakdown(basketId);
                    this.updateFlightInfo(basketId);
                });

                broker.subscribe('BookingSummary', 'updatePricesOnPUP', e => {
                    this.getCostBreakdown(basketId);
                    this.updateFlightInfo(basketId);
                });

                $scope.$on('$destroy', () => {
                    broker.unsubscribe('BookingSummary');
                });

                if (tripId) {
                    //need to override the package price setting to show flight cost breakdown
                    this.tripService.usePackageRates(tripId)
                    .then(packagePrice => this.packagePrice = packagePrice);
                    this.modifyEssentialsRoute = `/holidays/options/${this.tripId}`;
                }

                if (!this.priceDifference) {
                    this.priceDifference = 0;
                }
            };

            if (!$routeParams.tripId && this.basketId && this.tripId) {
                init(this.basketId, this.tripId);
            } else {
                tripService.getTrip($routeParams.tripId).then(trip => {
                    if (trip && trip.BasketId) {
                        init(trip.BasketId, trip.TripId);
                    }
                });
            }
            this.quoteRef = $routeParams.quoteRef;
            this.quoteNo = $routeParams.quoteNo;
        }

        private getProductInfo(basketId :string, tripId :string): void {

            this.$q.all([
                this.basketStore.all(basketId),
		        this.tripService.getTripReducedOccupancy(tripId)
            ]).then(results => {
                const basket = results[0];

                if (basket.h) {
                    const hotelFromBasket = basket.h;

                    this.hotelFromSession.getHotel(hotelFromBasket.AvailabilitySearchId,
                        hotelFromBasket.Details.Id,
                        tripId)
                        .then(hotel => {
                            this.hotel = angular.extend(hotel, hotelFromBasket);
                        });
                }

                this.occupancy = results[1];

                this.flight = basket.f;
                this.attraction = basket.a;
                this.carhire = basket.c;
                this.insurance = basket.i;
                this.parking = basket.p;
                this.transfer = basket.t;
            });

            this.updateFlightInfo(basketId);
            this.updateHotelInfo(basketId);
        }

        private updateFlightInfo(basketId :string): ng.IPromise<void> {
            return this.basketStore.flight(basketId)
                .then(flight => {
                    if (!flight) {
                        return;
                    }
                    this.flight = flight;
                    this.flightCostBreakdown = flight.CostBreakdown;
                    //remove atol cost from flight cost breakdown
                    const atol = _.find(this.flightCostBreakdown, (item: any) => {
                        return item.Description.indexOf('ATOL') >= 0;
                    });
                    if (atol) {
                        this.flightCostBreakdown = _.filter(this.flightCostBreakdown, item => item !== atol);
                    }
                    return;
                });
        }

        private updateHotelInfo(basketId: string): ng.IPromise<void> {
            return this.basketStore.hotel(basketId)
                .then(hotel => {
                    if (!hotel) {
                        return;
                    }

                    const costType = {
                        Unknown: 0,
                        Accommodation: 1,
                        Discount: 20,
                        AccommodationCancellationProtection: 21,
                        AccommodationLocalFeesAndTaxes: 22,
                        AccommodationExtraTransfer: 23,
                        AccommodationExtraEvent: 24,
                        AccommodationExtraAttraction: 25,
                        AccommodationExtra: 26,
                    };

                    const costBreakdown = hotel.CostBreakdown;

                    const extraCostItems = costBreakdown.filter(cost => {
                        const match = _.get(cost, 'ParentId') && _.get(cost, 'TypeId') ===
                        (costType.AccommodationExtraTransfer ||
                            costType.AccommodationExtraEvent ||
                            costType.AccommodationExtraAttraction ||
                            costType.AccommodationExtra ||
                            costType.AccommodationLocalFeesAndTaxes ||
                            costType.AccommodationCancellationProtection ||
                            costType.Discount);
                            return match;

                        }).filter(extra => extra);

                    const roomCostItems = costBreakdown.filter(cost => !_.get(cost, 'ParentId')
                    && _.get(cost, 'TypeId') === costType.Accommodation).map((room, LinkId) => {
                        return { ...room, LinkId };
                    });

                    if (extraCostItems && extraCostItems.length > 0 && roomCostItems && roomCostItems.length > 0) {

                        const updatedRoomCostItems = roomCostItems.map(room => {
                            return { RoomId: room.Id, RoomDescription: room.Description };
                        });

                        const enhancedExtras = extraCostItems.map(extras => {
                            return { ...extras, ...updatedRoomCostItems.find(acc => acc.RoomId === extras.ParentId) };
                        });

                        const updatedExtrasCostItems = enhancedExtras.map((extra, LinkId) => {
                            const splitDescription = _.get(extra, 'RoomDescription').toString().split('(');
                            const Description = splitDescription[1] ?
                                `${extra.Description} (${splitDescription[1]}` :
                                    extra.Description;
                            return {
                                ...extra,
                                Description,
                                LinkId,
                            };
                        });

                        const updatedCostBreakdown = [...roomCostItems, ...updatedExtrasCostItems];
                        const costsSortedById = updatedCostBreakdown.sort((a, b) => (a.LinkId >= b.LinkId) ? 1 : -1);
                        this.hotelCostBreakdown = costsSortedById;

                    } else {

                        this.hotelCostBreakdown = hotel.CostBreakdown.filter(item => {
                            return this.hotelExtrasTypeIds.indexOf(item.TypeId) !== -1;
                        });
                    }

                    this.hotelExtrasCostBreakdown = this.hotelCostBreakdown.filter(item => item.TypeId !== 1);

                    return;
                });
        }

        private getCostBreakdown(basketId :string): void {

            this.$q.all([
                this.basketStore.totalDiscount(basketId),
                this.basketStore.totalDeposit(basketId, this.$routeParams.ProductId),
                this.basketStore.costBreakdownGrouped(basketId),
                this.basketStore.rules(basketId, this.$routeParams.ProductId),
                this.basketStore.payDirectTotal(basketId),
		        this.basketStore.carHire(basketId),
                this.basketStore.isAtolProtected(basketId),
                this.basketStore.attractionCost(basketId)
            ]).then(([
                totalDiscount,
                totalDeposit,
                costBreakdownGrouped,
                rules,
                payDirectTotal,
                carHire,
                isAtolProtected,
                attractionsCost
            ]) => {

                this.totalDiscount = totalDiscount;
                this.totalDeposit = totalDeposit;
                this.costBreakdown = costBreakdownGrouped;
                this.totalCost = costBreakdownGrouped.total;
                this.atolAvailable = isAtolProtected;
                this.attractionsCost = attractionsCost;

                _.each(this.costBreakdown, (cb) => {
                    if (cb.group === 1) {
                        // HOTEL
                        this.hotelCost = cb.costBreakdown
                            .reduce((totalCost, currentHotel) =>
                                totalCost
                                + (currentHotel.UnitAmount > 0
                                    ? currentHotel.UnitAmount
                                    : (currentHotel.TypeId !== 20 ? currentHotel.UnitAmount : 0)),
                                0); /* id: 20 is discounts */
                        this.cancellationProtection = _.get(cb.costBreakdown.find(cost =>
                            cost.TypeId === this.costBreakdownCodes.CancellationProtection), 'UnitAmount', 0);
                        this.tax = cb.costBreakdown
                            .reduce((totalCost, currentHotel) =>
                                totalCost + (currentHotel.TypeId === this.cityTaxTypeId ? currentHotel.UnitAmount : 0),
                            0);
                        if (this.tax && this.tax > 0) {
                            this.hotelCost = this.hotelCost - this.tax;
                        }
                        if (this.cancellationProtection) {
                            this.hotelCost = this.hotelCost - this.cancellationProtection;
                        }
                        this.mandatoryTransfersCost = cb.costBreakdown
                            .reduce((totalCost, currentHotel) =>
                            totalCost + (currentHotel.TypeId === this.mandatoryTransferTypeId ?
                                         currentHotel.UnitAmount : 0),
                            0);
                    } else if (cb.group === 2) {
                        // FLIGHTS
                        // Isolate the ATOL cost
                        // Ideally we would be able to search by type but we have to use the description
                        const atol = _.find(cb.costBreakdown, (item: any) => {
                            return item.Description.indexOf('ATOL') >= 0;
                        });
                        if (atol) {
                            this.atolCost = atol.UnitAmount * atol.Quantity;
                            this.flightCost = cb.total - this.atolCost; // subtract the atol cost from the flights total
                        } else {
                            this.flightCost = cb.total;
                        }
                    } else if (cb.group === 3) {
                        // EXTRAS
                        this.extrasCost = cb.total;
                        this.extras = this.basketStore.extrasBreakdown(cb.costBreakdown);
                    }
                });

                this.hasDeposit = rules.depositAllowed;
                this.hasPayDirect = payDirectTotal > 0;

                let carHireExtras: any = _.get(carHire, 'RequestedItems');

                this.carHireExtras = carHireExtras;
                this.carHireExtrasTotal = carHireExtras ?
                    carHireExtras.reduce((total, extra) => total += extra.UnitAmount, 0) :
                    undefined;
            });

        }

        public editEssentials() {
            this.analytics.track('mixpanel', 'EmailMeEditQuote');
        }

        public showDetails(e) {
            const passengers = this.occupancyService.getTotalPeopleCounts();
            e.preventDefault();
            this.broker.publish('showModalFlightDetails', { flight: this.flight.Details.Itinerary, passengers });
        }
    }

    angular
        .module('core')
        .directive('bookingSummary', (): ng.IDirective => {

        return {
            scope: {},
            restrict: 'E',
            bindToController: {
                basketId: '=?',             //ignored if a TripId is available from the routeParams
                tripId: '=?',               //ignored if a TripId is available from the routeParams
                cardFeeForDeposit: '=?',
                cardFeeForTotal: '=?',
                changeFlightEnabled: '@?',
                continueToNextStep: '&?',
                occupancy: '=?',
                //for quotes we also watch out for price changes and tell the user so enable that functionality if
                //set. (i know not great but short of duplicating all of this code it seems like the best thing to do)
                priceDifference: '=?',
                modifyEssentials: '=?',
                currencyCode: '=',
                showAncillaries: '@?'
            },
            controller: BookingSummary,
            controllerAs: 'hbs',
            replace: true,
            templateUrl: (element, attr) => {
                if (attr.templateVersion) {
                    return 'partials/booking/template?name=BookingSummary&version='
                        + attr.templateVersion;
                }
                return 'partials/home/directivetemplate?type=BookingSummary';
            }
        };

    });

}
